import type {AnchorPosition, PositionSettings} from '@primer/behaviors'
import {getAnchoredPosition} from '@primer/behaviors'
import {useResizeObserver} from '@primer/react'
import {useCallback, useRef, useState} from 'react'

const lastDelimiterRegex = /:|,|\s(?=(?:[^"]*"[^"]*")*[^"]*$)/g

export interface AnchoredPositionHookSettings extends Partial<PositionSettings> {
  floatingElementRef?: React.RefObject<Element | null>
  caretElementRef?: React.RefObject<Element | null>
  inputElementRef: React.RefObject<HTMLInputElement | null>
}

/**
 * Calculates the top and left values for an absolutely-positioned floating element
 * to be anchored to some anchor element. Returns refs for the floating element
 * and the anchor element, along with the position.
 *
 * Position is computed lazily — the hook does not auto-compute on mount (which would force
 * an expensive reflow before the floating element is visible). Callers invoke `updatePosition`
 * when they need a fresh position (e.g. when showing suggestions or when the caret moves);
 * a resize observer also re-invokes it when the floating element resizes.
 *
 * @param settings Settings for calculating the anchored position.
 * @returns An object of {top: number, left: number} to absolutely-position the
 * floating element.
 */
export function useDelimiterAnchorPosition(settings?: AnchoredPositionHookSettings): {
  floatingElementRef: React.RefObject<Element | null>
  caretElementRef: React.RefObject<Element | null>
  position: AnchorPosition | undefined
  updatePosition: () => void
} {
  const createdFloatingElementRef = useRef<Element>(null)
  const floatingElementRef = settings?.floatingElementRef ?? createdFloatingElementRef
  const createdCaretElementRef = useRef<Element>(null)
  const caretElementRef = settings?.caretElementRef ?? createdCaretElementRef
  const createdInputElementRef = useRef<HTMLInputElement>(null)
  const inputElementRef = settings?.inputElementRef ?? createdInputElementRef
  // Destructure settings into primitives so the useCallback dep array can subscribe to actual
  // values, not the (caller-provided) settings object identity. Callers passing constant values
  // (the only current usage) get a stable updatePosition identity, preserving the perf intent
  // of the previous implementation without the hand-tuned dep-array spread that needed a lint
  // disable.
  const {side, align, alignmentOffset, anchorOffset, allowOutOfBounds} = settings || {}
  const [position, setPosition] = useState<AnchorPosition | undefined>(undefined)

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const updatePosition = useCallback(() => {
    const inputElement = inputElementRef.current
    let delimiter: Element | null = null

    if (inputElement) {
      const delimiters = Array.from(
        inputElement.closest('.styled-input-container')?.querySelectorAll('.delimiter') ?? [],
      )
      const filter = inputElement.value
      const selectionStart = inputElement.selectionStart
      if (filter && selectionStart) {
        const beforeCursorStr = filter.slice(0, selectionStart)
        const matches = [...beforeCursorStr.matchAll(lastDelimiterRegex)]
        if (matches.length < 1) {
          delimiter = inputElement
        } else {
          let match: Element | undefined
          for (let i = matches.length; i > 0; i--) {
            const candidate = delimiters[i - 1]
            if (candidate && !candidate.classList.contains('text-delimiter')) {
              match = candidate
              break
            }
          }
          delimiter = match ?? inputElement
        }
      }
    }

    // Fall back to the input element when no delimiter was resolved (e.g. empty filter or caret at
    // position 0) so the floating element stays anchored,
    delimiter = delimiter ?? inputElement

    if (floatingElementRef.current instanceof Element && delimiter instanceof Element) {
      setPosition(
        getAnchoredPosition(floatingElementRef.current, delimiter, {
          side,
          align,
          alignmentOffset,
          anchorOffset,
          allowOutOfBounds,
        }),
      )
    } else {
      setPosition(undefined)
    }
  }, [floatingElementRef, inputElementRef, side, align, alignmentOffset, anchorOffset, allowOutOfBounds])

  useResizeObserver(updatePosition)

  return {
    floatingElementRef,
    caretElementRef,
    position,
    updatePosition,
  }
}
